import { getViewType } from 'widgets/toolbox/viewtype';
import { extractPath } from 'core/utils';
import { TRefElementInstance, RefElement } from 'widgets/toolbox/RefElement';
import { getCookie } from 'widgets/toolbox/cookie';

// Time after the last pointer-driven scroll during which residual momentum still counts as a swipe.
const SWIPE_MOMENTUM_GRACE_MS = 700;

/**
 * @param Carousel Base widget for extending
 * @returns CoreCarousel widget
 */
function CoreCarouselClassCreator(Carousel: import('widgets/global/Carousel').TCarousel) {
    /**
     * @category widgets
     * @subcategory global
     * @class Carousel
     * @augments Carousel
     */
    class CoreCarousel extends Carousel {
        currentZoomPage = 0;

        hasTrackedSwipe = false;

        ignoreArrowButtonsClick = false;

        lastReportedPageIndex = 0;

        isSwipeInteraction = false;

        isPaginationClicked = false;

        private isTrackPointerDown = false;

        private lastTouchScrollTime = 0;

        private isScrollSequenceActive = false;

        prefs() {
            return {
                rtl: false,
                menuItemClass: '.b-menu_bar-item',
                itemTextClass: '.b-menu_models-tile_title',
                ...super.prefs()
            };
        }

        /**
         * @description Carousel page click handler
         * @param el page element
         * @listens dom#click
         * @emits Carousel#pageclicked
         */
        onPageClick(el) {
            if (this.delta === 0) {
                /**
                 * @description Event to carousel page click
                 * @event Carousel#pageclicked
                 */
                this.currentZoomPage = el.data('zoom-page') ?? 0;
                this.emit('pageclicked', el.data('page'), el.data('zoom-page'));
            }
        }

        /**
         * @description Scroll carousel by one page in the given direction
         * @param direction 1 for next, -1 for previous
         */
        private scrollByPage(direction: 1 | -1) {
            const elemCarouselTrack = this.ref(this.prefs().elemCarouselTrack).get();

            if (!elemCarouselTrack || this.ignoreArrowButtonsClick) {
                return;
            }

            this.ignoreArrowButtonsClick = true;

            if (this.carouselDirection === 'horizontal') {
                this.scrollHorizontalByPage(elemCarouselTrack, direction);
            } else {
                // For vertical carousels, simply scroll by one full page height
                elemCarouselTrack.scrollBy(0, direction * this.carouselDimension);
            }

            this.isNavButtonClicked = true;
        }

        /**
         * @description Handles horizontal scrolling by one page in the given direction.
         * Calculates the appropriate scroll amount based on the current scroll position,
         * the total scrollable width, and the visible carousel dimension.
         * Ensures the scroll amount does not exceed the remaining scrollable area
         * in either direction.
         *
         * @param elemCarouselTrack - The carousel track DOM element to scroll
         * @param direction - 1 for next (forward), -1 for previous (backward)
         */
        private scrollHorizontalByPage(elemCarouselTrack: HTMLElement, direction: 1 | -1) {
            const currentScrollLeft = elemCarouselTrack.scrollLeft;
            const totalScrollableWidth = elemCarouselTrack.scrollWidth;
            const visibleWidth = this.carouselDimension;

            /**
             * The absolute value of the current horizontal scroll position.
             * Falls back to the carousel's visible dimension if scrollLeft is 0
             * (e.g., at the very start).
             */
            // How far the user has already scrolled from the start (always positive)
            let absScrollLeft = Math.abs(currentScrollLeft) > 1 ? Math.abs(currentScrollLeft) : visibleWidth;

            /**
             * The remaining scrollable distance from the current position to the end.
             * Calculated as: totalScrollWidth - currentAbsPosition - visibleWidth.
             * Falls back to the carousel's visible dimension if the result is 0.
             */
            // How much scrollable content remains beyond the current viewport
            let remaining = Math.abs(totalScrollableWidth - absScrollLeft - visibleWidth) || visibleWidth;

            /**
             * When scrolling further in the current scroll direction
             * (i.e., negative scrollLeft going more negative, or positive going more positive),
             * clamp absScrollLeft to at least one full page so we don't under-scroll.
             */
            // When scrolling further in the current scroll direction,
            // ensure we scroll at least one full page worth
            const isScrollingFurtherInCurrentDirection = (currentScrollLeft < 0 && direction === -1) || (currentScrollLeft > 0 && direction === 1);

            if (isScrollingFurtherInCurrentDirection && absScrollLeft < visibleWidth) {
                absScrollLeft = visibleWidth;
            }

            /**
             * When scrolling back toward the origin
             * (i.e., negative scrollLeft going toward 0, or positive scrollLeft going toward 0),
             * clamp remaining to at least one full page so we don't under-scroll.
             */
            // When scrolling back toward the origin,
            // ensure remaining is at least one full page to avoid tiny scroll jumps
            const isScrollingBackTowardOrigin = (currentScrollLeft < 0 && direction === 1) || (currentScrollLeft > 0 && direction === -1);

            if (isScrollingBackTowardOrigin && remaining < visibleWidth) {
                remaining = visibleWidth;
            }

            /**
             * The final scroll amount is the minimum of:
             * - the full carousel visible dimension (one page),
             * - the absolute scroll position (to avoid scrolling past the start), and
             * - the remaining distance (to avoid scrolling past the end).
             */
            // Scroll by the smallest of: one full page, distance already scrolled, or distance remaining.
            // This prevents over-scrolling at the beginning or end of the track.
            const scrollAmount = Math.min(visibleWidth, absScrollLeft, remaining);

            elemCarouselTrack.scrollBy(direction * scrollAmount, 0);
        }

        /**
         * @description Scroll carousel to the next page
         * @listens dom#click
         */
        scrollToNextPage() {
            this.scrollByPage(1);
        }

        /**
         * @description Scroll carousel to the previous page
         * @listens dom#click
         */
        scrollToPrevPage() {
            this.scrollByPage(-1);
        }

        /**
         * @description Initial carousel configuration. Adds pointer-release listeners so the
         * track knows when a touch gesture ends (touchend is not bound via ISML).
         */
        initCarousel() {
            super.initCarousel();

            const track = this.ref(this.prefs().elemCarouselTrack).get();

            if (track) {
                this.ev('touchend', this.onTrackPointerUp, track);
                this.ev('touchcancel', this.onTrackPointerUp, track);
            }
        }

        /**
         * @description Clear the pointer-down flag when a touch gesture ends.
         * @listens dom#touchend
         * @listens dom#touchcancel
         */
        onTrackPointerUp() {
            this.isTrackPointerDown = false;
        }

        /**
         * @description On carousel scroll handler. Tracks pointer-driven scrolls so scrollbar-track
         * clicks are not misreported as swipes. The interaction type is locked once per scroll
         * sequence (at its first scroll event) so post-release momentum keeps the same type.
         * @listens dom#scroll
         * @listens dom#touchstart
         * @param _el event source element
         * @param event DOM event that triggered the scroll handler
         */
        onScroll(_el?: TRefElementInstance, event?: Event) {
            if (event?.type === 'touchstart') {
                this.isTrackPointerDown = true;
            } else if (this.isTrackPointerDown) {
                // A scroll while the pointer is held on the track is a genuine drag/swipe.
                this.lastTouchScrollTime = Date.now();
            }

            if (!this.isScrollSequenceActive) {
                this.isScrollSequenceActive = true;
                this.isSwipeInteraction = this.isTrackPointerDown || Date.now() - this.lastTouchScrollTime < SWIPE_MOMENTUM_GRACE_MS;
            }

            super.onScroll();
        }

        /**
         * @description Mouse down handler. Marks the pointer as held on the track so a following
         * drag-driven page change is not misreported as a scrollbar click.
         * @listens dom#mousedown
         * @param el event source element
         * @param event mouse event
         */
        onMouseDown(el: TRefElementInstance, event: MouseEvent) {
            this.isTrackPointerDown = true;

            super.onMouseDown(el, event);
        }

        /**
         * @description Mouse up handler. Clears the pointer-down flag when a drag ends.
         * @listens dom#mouseup
         */
        onMouseUp() {
            this.isTrackPointerDown = false;

            super.onMouseUp();
        }

        /**
         * @description Pagination (mobile scrollbar) click handler. Flags the page change so it can
         * be reported distinctly from a swipe.
         * @listens dom#click
         * @param el pagination dot element
         */
        handlePaginationClick(el: TRefElementInstance) {
            this.isPaginationClicked = true;

            super.handlePaginationClick(el);
        }

        /**
         * @description On carousel scroll end handler
         * @emits Carousel#pagechanged
         */
        onScrollEnd() {
            const currentPageIndex = this.getCurrentPageIndex();

            if (this.isNavButtonClicked) {
                this.emit('pagechanged', currentPageIndex);
            } else if (this.pagination && currentPageIndex !== this.lastReportedPageIndex) {
                this.emit('pagechanged', currentPageIndex);
            }

            this.lastReportedPageIndex = currentPageIndex;

            const hasScrolled = this.getCurrentScrollPosition() > 0;

            if (!this.hasTrackedSwipe && hasScrolled && window.contexts.includes('plp')) {
                this.hasTrackedSwipe = true;
                this.eventBus().emit('plp.image.swipe');
            }

            super.onScrollEnd();

            this.ignoreArrowButtonsClick = false;
            this.isScrollSequenceActive = false;
            this.isPaginationClicked = false;
        }

        /**
         * @description Update carousel during scroll
         */
        updateDuringScroll() {
            this.ignoreArrowButtonsClick = true;

            super.updateDuringScroll();
        }

        /**
         * @description Update carousel metric
         */
        updateCarouselMetric() {
            const isRTL = this.prefs().rtl;
            const carouselTrackElem = this.ref(this.prefs().elemCarouselTrack).get();
            const carouselElem = this.ref('self').get();

            const roundingDelta = this.roundingDelta;

            if (carouselTrackElem && carouselElem) {
                if (this.carouselDirection === 'horizontal') {
                    const totalScrollWidth = Math.abs(carouselTrackElem.scrollLeft) + carouselElem.offsetWidth;

                    if (isRTL ? carouselTrackElem.scrollLeft <= 1 : carouselTrackElem.scrollLeft >= -1) {
                        // We are comparing with 1 instead of 0 as Chrome has a problem calculating the width with RTL text direction
                        this.isScrollStart = Math.abs(carouselTrackElem.scrollLeft) <= 1;
                        this.isScrollEnd = totalScrollWidth + roundingDelta >= carouselTrackElem.scrollWidth;
                    }
                } else {
                    const totalScrollHeight = carouselTrackElem.scrollTop + carouselElem.offsetHeight;

                    this.isScrollStart = carouselTrackElem.scrollTop <= 0;
                    this.isScrollEnd = totalScrollHeight + roundingDelta >= carouselTrackElem.scrollHeight;
                }
            }
        }

        /**
         * @description Loads deferred images inside carousel by populating src from data-src attributes
         */
        public loadImages(): void {
            const container = this.ref('self').get();

            if (!container) {
                return;
            }

            const deferredImages = Array.from(container.querySelectorAll<HTMLImageElement>('img[data-src],source[data-srcset]'));

            deferredImages.forEach(img => {
                if (img.dataset.src) {
                    img.src = img.dataset.src;
                    delete img.dataset.src;
                }

                if (img.dataset.srcset) {
                    img.srcset = img.dataset.srcset;
                    delete img.dataset.srcset;
                }
            });
        }

        /**
         * @description Calculates width of the slide based on the first slide element.
         * This is used to determine how much to scroll on next/prev button click.
         */

        getSlideWidth(): number {
            const carouselTrack = this.ref(this.prefs().elemCarouselTrack).get();
            const firstSlide = carouselTrack?.querySelector<HTMLElement>('.b-carousel-item');

            if (!firstSlide) {
                return 0;
            }

            const style = window.getComputedStyle(firstSlide);
            const marginRight = parseFloat(style.marginRight || '0');

            return firstSlide.offsetWidth + marginRight;
        }

        /**
         * @description Scroll carousel to the next slide in universal recommendations
         * @listens dom#click
         */

        scrollToNextSlide() {
            this.scrollToSlide('next');
        }

        /**
         * @description Scroll carousel to the previous slide in universal recommendations
         * @listens dom#click
         */

        scrollToPrevSlide() {
            this.scrollToSlide('prev');
        }

        /**
         * @description Scroll carousel to the next or previous slide
         * @param direction 'next' or 'prev'
         */

        scrollToSlide(direction: 'next' | 'prev') {
            const carouselTrack = this.ref(this.prefs().elemCarouselTrack).get();

            if (!carouselTrack) {
                return;
            }

            const viewType = getViewType();
            const isMobile = viewType === 'small' || viewType === 'medium';

            let scrollAmount = 0;

            if (isMobile || this.ref('html').get()?.classList.contains('m-has_dialog')) {
                scrollAmount = this.getSlideWidth(); // scroll 1 slide on mobile and for drawer popups
            } else {
                scrollAmount = this.carouselDimension; // scroll full container on desktop
            }

            if (this.carouselDirection === 'horizontal') {
                carouselTrack.scrollTo({
                    left: direction === 'next' ? carouselTrack.scrollLeft + scrollAmount : carouselTrack.scrollLeft - scrollAmount,
                    behavior: 'smooth'
                });
            } else {
                carouselTrack.scrollTo({
                    top: direction === 'next' ? carouselTrack.scrollTop + scrollAmount : carouselTrack.scrollTop - scrollAmount,
                    behavior: 'smooth'
                });
            }

            this.isNavButtonClicked = true;
        }

        /**
         * @description Track click on menu content
         */
        menuModelsClick(modelItem: RefElement<HTMLElement>) {
            const url = extractPath(modelItem.attr('href'));
            const categoryId = modelItem?.get()?.closest(this.prefs().menuItemClass)?.getAttribute('data-category-id');

            if (modelItem) {
                this.eventBus().emit('menu.models.click', url, categoryId);
            }
        }

        /**
         * @description Programmatically handle scroll to focused carousel item.
         * This is the fix for Chrome bug where it could not calculate item boundaries properly
         * and threat item like it into view. The bug is on PDP Gallery.
         * Applicable only for horizontal scroll.
         */
        handleScrollToFocusedItem() {
            if (this.carouselDirection !== 'horizontal') {
                return;
            }

            const track = this.ref(this.prefs().elemCarouselTrack).get();
            const activeItem = document.activeElement;

            if (!activeItem || !(activeItem instanceof HTMLElement) || !track) {
                return;
            }

            const activeItemLeft = activeItem.getBoundingClientRect().left;
            const activeItemRight = activeItemLeft + activeItem.getBoundingClientRect().width;
            const trackStart = track.getBoundingClientRect().left;
            const trackEnd = trackStart + track.clientWidth;

            if (Math.trunc(activeItemRight) > Math.trunc(trackEnd)) {
                track.scrollBy(activeItemRight - trackEnd, 0);
            } else if (activeItemLeft <= trackStart) {
                track.scrollBy(activeItemLeft - trackStart, 0);
            }
        }

        /**
         * Enables the current slide in the carousel by updating the class and tabindex
         * attributes of the carousel track's child elements based on the provided page index.
         *
         * @param pageIndex - The index of the page to be set as the current slide.
         * This is used to match the `data-page` attribute of the
         * carousel track's child elements.
         * @returns The current instance of the class, allowing for method chaining.
         */
        enableCurrentSlide(pageIndex: number) {
            const elemCarouselTrack = this.ref(this.prefs().elemCarouselTrack).get();

            Array.prototype.slice.call(elemCarouselTrack?.children).forEach((element: HTMLElement) => {
                const dataPage = Number.parseInt(element.dataset.page ?? '0', 10);

                element.classList.toggle(this.prefs().pageCurrentClass, dataPage === pageIndex);
                element.setAttribute('tabindex', dataPage === pageIndex ? '0' : '-1');
            });

            return this;
        }

        /**
         * @description Next/Prev Buttons keyboard handler. Used only on PDP gallery
         * we need this method to prevent Zoom opened on Enter and able to focus carousel pagination
         * @listens dom#keydown
         * @param element dom node that trigger the event
         * @param event  Event object
         */
        handleKeydown(element: TRefElementInstance, event: KeyboardEvent) {
            const isUsableNetAssistive = Boolean(getCookie('UsableNetAssistive'));
            const htmlEl = element.get();
            const isEnterOrSpace = event.key === 'Enter' || event.key === ' ';

            if (isUsableNetAssistive && htmlEl && isEnterOrSpace) {
                event.stopPropagation();

                if (htmlEl.dataset.eventClick === 'scrollToNextPage') {
                    this.scrollToNextSlide();
                } else {
                    this.scrollToPrevSlide();
                }
            } else if (!isUsableNetAssistive && isEnterOrSpace) {
                event.stopPropagation();
                this.onPageClick(element);
            }
        }
    }

    return CoreCarousel;
}

export type TCoreCarousel = ReturnType<typeof CoreCarouselClassCreator>;

export type TCoreCarouselInstance = InstanceType<TCoreCarousel>;

export default CoreCarouselClassCreator;
